import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  ArrowLeft,
  SignOut,
  ArrowClockwise,
  Phone,
  EnvelopeSimple,
  MapPin,
  Calendar,
  ChatCircleText,
  PaperPlaneTilt,
  DownloadSimple,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { LEAD_STATUSES, LOGO_ICON } from "@/lib/brand";

const STATUS_STYLES = {
  New: "bg-sky-100 text-sky-700 border-sky-200",
  "In Progress": "bg-amber-100 text-amber-800 border-amber-200",
  Completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Archived: "bg-slate-200 text-slate-700 border-slate-300",
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const CSV_COLUMNS = [
  "id",
  "createdAt",
  "name",
  "phone",
  "email",
  "addressOrArea",
  "serviceType",
  "frequency",
  "status",
  "message",
];

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildLeadsCsv(rows) {
  const header = CSV_COLUMNS.join(",");
  const body = rows
    .map((r) => CSV_COLUMNS.map((c) => csvEscape(r[c])).join(","))
    .join("\r\n");
  // BOM so Excel opens UTF-8 correctly
  return "\uFEFF" + header + "\r\n" + body + "\r\n";
}

function downloadLeadsCsv(rows, status, search) {
  if (!rows || rows.length === 0) {
    toast.message("No leads to export with the current filters.");
    return;
  }
  const today = new Date().toISOString().slice(0, 10);
  const csv = buildLeadsCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `heavenly-leads-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  const filterLabel = [
    status && status !== "All" ? `status=${status}` : null,
    search && search.trim() ? `search="${search.trim()}"` : null,
  ]
    .filter(Boolean)
    .join(", ");
  toast.success(
    `Exported ${rows.length} lead${rows.length === 1 ? "" : "s"}${
      filterLabel ? ` (${filterLabel})` : ""
    }.`
  );
}

export default function AdminLeads() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status && status !== "All") params.status = status;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/admin/leads", { params });
      setLeads(data);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't load leads.");
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => fetchLeads(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const counts = useMemo(() => {
    const out = { All: leads.length };
    for (const s of LEAD_STATUSES) out[s] = leads.filter((l) => l.status === s).length;
    return out;
  }, [leads]);

  const updateStatus = async (leadId, newStatus) => {
    setUpdating(true);
    try {
      const { data } = await api.patch(`/admin/leads/${leadId}`, { status: newStatus });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? data : l)));
      setSelected((s) => (s && s.id === leadId ? data : s));
      toast.success(`Status updated to ${newStatus}.`);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't update status.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-leads-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_ICON} alt="" className="w-9 h-9 rounded-full" />
            <div className="min-w-0">
              <div className="font-display font-bold text-slate-900 truncate">Leads Dashboard</div>
              <div className="text-xs text-slate-500 truncate">
                Signed in as {user?.email || "admin"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-full"
              data-testid="admin-back-to-site"
            >
              <ArrowLeft size={14} /> Public site
            </Link>
            <button
              onClick={() => fetchLeads()}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 px-3 py-2 rounded-full"
              data-testid="admin-refresh"
            >
              <ArrowClockwise size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm text-white bg-slate-900 hover:bg-slate-800 px-3 py-2 rounded-full"
              data-testid="admin-logout"
            >
              <SignOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">Leads</h1>
            <p className="text-sm text-slate-500 mt-1">
              {leads.length} {leads.length === 1 ? "submission" : "submissions"} shown
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
            <div className="relative w-full sm:w-72">
              <MagnifyingGlass
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, phone, or message"
                className="pl-9"
                data-testid="admin-search-input"
              />
            </div>
            <button
              type="button"
              onClick={() => downloadLeadsCsv(leads, status, search)}
              disabled={leads.length === 0}
              className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-full transition-all whitespace-nowrap"
              data-testid="admin-download-csv"
              title={leads.length === 0 ? "No leads to export" : "Download filtered leads as CSV"}
            >
              <DownloadSimple size={16} weight="bold" />
              Download CSV
            </button>
          </div>
        </div>

        <Tabs value={status} onValueChange={setStatus} className="mb-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-full flex-wrap h-auto">
            {["All", ...LEAD_STATUSES].map((s) => (
              <TabsTrigger
                key={s}
                value={s}
                className="rounded-full data-[state=active]:bg-sky-500 data-[state=active]:text-white px-4 py-1.5 text-sm"
                data-testid={`filter-${s.replace(" ", "-")}`}
              >
                {s}
                <span className="ml-2 text-xs opacity-70">{counts[s] ?? 0}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="whitespace-nowrap">Created</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="whitespace-nowrap">Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="whitespace-nowrap">Service</TableHead>
                  <TableHead className="whitespace-nowrap">Frequency</TableHead>
                  <TableHead className="whitespace-nowrap">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-12">
                      Loading leads…
                    </TableCell>
                  </TableRow>
                ) : leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-12" data-testid="leads-empty">
                      No leads match these filters yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((l) => (
                    <TableRow
                      key={l.id}
                      onClick={() => setSelected(l)}
                      className="cursor-pointer hover:bg-sky-50/60"
                      data-testid={`lead-row-${l.id}`}
                    >
                      <TableCell className="whitespace-nowrap text-sm text-slate-600">
                        {formatDate(l.createdAt)}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">{l.name}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-slate-600">{l.phone}</TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[220px] truncate">{l.email}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-slate-600">{l.serviceType}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-slate-600">{l.frequency}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`${STATUS_STYLES[l.status] || ""} font-medium`}
                        >
                          {l.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="sm:max-w-xl overflow-y-auto" data-testid="lead-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl">{selected.name}</SheetTitle>
                <SheetDescription className="flex items-center gap-2 text-slate-500">
                  <Calendar size={14} /> {formatDate(selected.createdAt)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                    Status
                  </span>
                  <Select
                    value={selected.status}
                    onValueChange={(v) => updateStatus(selected.id, v)}
                    disabled={updating}
                  >
                    <SelectTrigger className="w-44" data-testid="lead-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`lead-status-option-${s.replace(" ", "-")}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ContactTile icon={Phone} label="Phone" value={selected.phone} href={`tel:${selected.phone}`} />
                  <ContactTile
                    icon={EnvelopeSimple}
                    label="Email"
                    value={selected.email}
                    href={`mailto:${selected.email}`}
                  />
                </div>

                <ContactTile icon={MapPin} label="Address / Area" value={selected.addressOrArea} />

                <div className="grid grid-cols-2 gap-4">
                  <InfoTile label="Service" value={selected.serviceType} />
                  <InfoTile label="Frequency" value={selected.frequency} />
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
                    Message
                  </div>
                  <div className="bg-stone-50 border border-slate-100 rounded-2xl p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="lead-message">
                    {selected.message}
                  </div>
                </div>

                <NotesPanel leadId={selected.id} />

                <div className="text-xs text-slate-400 pt-4 border-t border-slate-100">
                  Lead ID: {selected.id}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ContactTile({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-center gap-3 p-3 rounded-2xl border border-slate-100 bg-white hover:border-sky-200 transition-colors">
      <div className="w-9 h-9 rounded-xl bg-sky-500/10 text-sky-600 flex items-center justify-center">
        <Icon size={18} weight="bold" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-sm text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block">
      {inner}
    </a>
  ) : (
    inner
  );
}

function InfoTile({ label, value }) {
  return (
    <div className="p-3 rounded-2xl border border-slate-100 bg-white">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm text-slate-900">{value}</div>
    </div>
  );
}

function NotesPanel({ leadId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/leads/${leadId}/notes`);
      setNotes(data);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't load notes.");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/leads/${leadId}/notes`, { noteText: value });
      setNotes((prev) => [data, ...prev]);
      setText("");
      toast.success("Note added.");
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="lead-notes-section">
      <div className="flex items-center gap-2 mb-3">
        <ChatCircleText size={16} weight="duotone" className="text-sky-600" />
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          Internal Notes
        </div>
        <span className="text-[11px] text-slate-400">({notes.length})</span>
      </div>

      <form onSubmit={submit} className="space-y-2 mb-4" data-testid="add-note-form">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Add a note about this lead — calls, follow-ups, quoted price, etc."
          data-testid="note-textarea"
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || !text.trim()}
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white text-sm font-semibold px-4 py-2 rounded-full transition-all"
            data-testid="add-note-button"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {saving ? "Saving…" : "Add Note"}
          </button>
        </div>
      </form>

      {loading && notes.length === 0 ? (
        <div className="text-sm text-slate-400 italic">Loading notes…</div>
      ) : notes.length === 0 ? (
        <div className="text-sm text-slate-400 italic" data-testid="notes-empty">
          No notes yet — add the first one above.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="notes-list">
          {notes.map((n) => (
            <li
              key={n.id}
              className="bg-white border border-slate-100 rounded-2xl p-4"
              data-testid={`note-${n.id}`}
            >
              <div className="flex items-center justify-between gap-2 text-[11px] mb-1.5">
                <span className="font-semibold text-slate-700 uppercase tracking-widest">
                  {n.author}
                </span>
                <span className="text-slate-400">
                  {new Date(n.createdAt).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {n.noteText}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
